import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, CheckCircle2, Copy, Send } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { parseAmount } from '@aeris/finance'
import { isPositive } from '@aeris/money'
import { isValidCnpj, isValidCpf } from '@aeris/organization'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  MoneyInput,
} from '@aeris/ui'

import { fetchConversationDetail } from '../lib/api/communication-contacts.ts'
import { sendConversationMessage } from '../lib/api/communication-conversations.ts'
import { fetchParty } from '../lib/api/parties.ts'
import { ApiError } from '../lib/api.ts'
import { money, pixMessage, pixMessages } from '../lib/inbox-commerce.ts'
import {
  createPixCharge,
  fetchPixCharge,
  type PixCharge,
} from '../lib/pix-charge.ts'

import { PixQr } from './pix-qr.tsx'

/**
 * Gerar Pix pelo atendimento — F204, pela Monedd.
 *
 * A Monedd pede nome, CPF e e-mail de quem paga. Vêm do cliente vinculado à
 * conversa (decisão do dono), e o vendedor completa o que faltar. Depois de
 * gerado: o QR, o copia-e-cola, e o status — a Monedd não avisa quando foi
 * pago, então a janela pergunta ao servidor a cada 5 s enquanto está aberta.
 */

const POLL_MS = 5_000

const digits = (value: string): string => value.replace(/\D/g, '')

const validDocument = (value: string): boolean => {
  const only = digits(value)
  return (
    (only.length === 11 && isValidCpf(only)) ||
    (only.length === 14 && isValidCnpj(only))
  )
}

export const PixChargeDialog = ({
  conversationId,
  onClose,
  onInsert,
}: {
  conversationId: string | null
  onClose: () => void
  onInsert: (text: string) => void
}): ReactNode => {
  const [amount, setAmount] = useState('')
  /*
   * `null` é "ainda não mexeu": vale o que veio do cliente vinculado. Assim o
   * cliente pode chegar depois da janela abrir sem apagar o que já digitaram.
   */
  const [nameInput, setName] = useState<string | null>(null)
  const [documentInput, setDocument] = useState<string | null>(null)
  const [emailInput, setEmail] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const queryClient = useQueryClient()

  const detail = useQuery({
    queryKey: ['communication-conversation-detail', conversationId],
    queryFn: async ({ signal }) =>
      await fetchConversationDetail(conversationId ?? '', signal),
    enabled: conversationId !== null,
  })
  const party = detail.data?.party ?? null
  const partyDetail = useQuery({
    queryKey: ['party', party?.id],
    queryFn: async ({ signal }) => await fetchParty(party?.id ?? '', signal),
    enabled: party !== null,
  })

  const name =
    nameInput ??
    (party === null ? '' : (party.tradeName ?? party.legalName).slice(0, 50))
  const document = documentInput ?? party?.document ?? ''
  const email =
    emailInput ??
    partyDetail.data?.party.contacts.find((contact) => contact.kind === 'email')
      ?.value ??
    ''

  const generate = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async () =>
      await createPixCharge({
        conversationId,
        partyId: party?.id ?? null,
        amount,
        payer: {
          name: name.trim(),
          document: digits(document),
          // Em branco, o servidor usa o e-mail da conta Monedd da loja.
          email: email.trim() === '' ? null : email.trim(),
        },
      }),
  })
  const created = generate.data

  const status = useQuery({
    queryKey: ['pix-charge', created?.id],
    queryFn: async ({ signal }) =>
      await fetchPixCharge(created?.id ?? '', signal),
    enabled: created !== undefined,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' || query.state.data === undefined
        ? POLL_MS
        : false,
  })
  const charge = status.data ?? created

  /*
   * "Enviar ao cliente" (F207): as DUAS mensagens do sistema antigo — o link,
   * e o copia-e-cola sozinho. Uma depois da outra, na ordem; é o vendedor que
   * clica, nada sai sozinho.
   */
  const send = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).
    meta: { errorShownInline: true },
    mutationFn: async (target: PixCharge) => {
      for (const body of pixMessages(target)) {
        await sendConversationMessage(conversationId ?? '', { body })
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['communication-messages', conversationId],
      })
      void queryClient.invalidateQueries({
        queryKey: ['communication-conversations'],
      })
    },
  })

  const amountOk = amount.trim() !== '' && isPositive(parseAmount(amount))
  const documentOk = validDocument(document)
  const emailOk =
    email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const ready = amountOk && name.trim() !== '' && documentOk && emailOk

  const failure =
    generate.error instanceof ApiError
      ? generate.error.message
      : generate.isError
        ? 'Não foi possível gerar o Pix agora.'
        : null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Gerar Pix"
        description="A cobrança sai pela Monedd. O código vai para a mensagem, e você confere antes de enviar."
      >
        {charge === undefined ? (
          <div className="flex flex-col gap-3">
            <Field label="Valor">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={amount}
                  onValueChange={setAmount}
                  autoFocus
                />
              )}
            </Field>
            <Field label="Nome de quem paga">
              {(field) => (
                <Input
                  {...field}
                  maxLength={50}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                  }}
                />
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="CPF ou CNPJ"
                error={
                  document !== '' && !documentOk
                    ? 'Documento inválido.'
                    : undefined
                }
              >
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    maxLength={18}
                    value={document}
                    onChange={(event) => {
                      setDocument(event.target.value)
                    }}
                  />
                )}
              </Field>
              <Field
                label="E-mail (opcional)"
                hint="Em branco, vai o e-mail da loja."
              >
                {(field) => (
                  <Input
                    {...field}
                    type="email"
                    maxLength={50}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value)
                    }}
                  />
                )}
              </Field>
            </div>
            {party === null && conversationId !== null && !detail.isPending ? (
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Esta conversa não tem cliente vinculado — preencha os dados de
                quem vai pagar.
              </p>
            ) : null}
            {failure === null ? null : (
              <InlineAlert tone="danger">{failure}</InlineAlert>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {charge.status === 'paid' ? (
              <InlineAlert tone="success">
                <span className="flex items-center gap-2">
                  <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                  Pago
                  {charge.paidAt === null
                    ? ''
                    : ` às ${new Date(charge.paidAt).toLocaleTimeString(
                        'pt-BR',
                        {
                          hour: '2-digit',
                          minute: '2-digit',
                        }
                      )}`}
                </span>
              </InlineAlert>
            ) : charge.status === 'pending' ? (
              <p
                role="status"
                className="text-(length:--text-sm) text-(--text-secondary)"
              >
                Aguardando o pagamento de{' '}
                <strong className="font-(--font-mono) tabular-nums">
                  {money(charge.amount)}
                </strong>
                …
              </p>
            ) : (
              <InlineAlert tone="danger">
                A Monedd diz que este Pix não vale mais (
                {charge.providerStatus ?? charge.status}).
              </InlineAlert>
            )}

            {charge.paymentUrl === null ? (
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Sem link de pagamento: o servidor não tem endereço público
                (PUBLIC_BASE_URL). Vai só o copia-e-cola.
              </p>
            ) : (
              <p className="flex flex-wrap items-center gap-2 text-(length:--text-sm)">
                <a
                  href={charge.paymentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all underline underline-offset-2"
                >
                  {charge.paymentUrl}
                </a>
              </p>
            )}
            <PixQr text={charge.copyPaste} />
            <code
              aria-label="Pix copia e cola"
              className="break-all rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-1 text-(length:--text-xs)"
            >
              {charge.copyPaste}
            </code>
            <div>
              <Button
                variant="ghost"
                size="sm"
                leadingIcon={
                  copied ? (
                    <Check aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <Copy aria-hidden="true" className="h-4 w-4" />
                  )
                }
                onClick={() => {
                  void navigator.clipboard
                    .writeText(charge.copyPaste)
                    .then(() => {
                      setCopied(true)
                    })
                }}
              >
                {copied ? 'Copiado' : 'Copiar código'}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {charge === undefined ? 'Cancelar' : 'Fechar'}
          </Button>
          {charge === undefined ? (
            <Button
              variant="primary"
              disabled={!ready}
              loading={generate.isPending}
              onClick={() => {
                generate.mutate()
              }}
            >
              Gerar
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  onInsert(pixMessage(charge))
                }}
              >
                Pôr na mensagem
              </Button>
              {conversationId === null ? null : (
                <Button
                  variant="primary"
                  loading={send.isPending}
                  disabled={send.isSuccess}
                  leadingIcon={<Send aria-hidden="true" className="h-4 w-4" />}
                  onClick={() => {
                    send.mutate(charge)
                  }}
                >
                  {send.isSuccess ? 'Enviado' : 'Enviar ao cliente'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
        {send.isError ? (
          <InlineAlert tone="danger">
            {send.error instanceof ApiError
              ? send.error.message
              : 'Não foi possível enviar agora.'}
          </InlineAlert>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
